/**
 * NCR Edit save / submit handlers (Detail, Root Cause Analysis, Corrective
 * Action Report), split out of useNCREdit.
 */

import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { getApiFieldErrors } from '../../utils/errorUtils';
import { ncrFieldMessage } from '../ncr/ncrCreateErrors';
import { NCREditFormData } from './types';
import { RCA_REQUIRED_FIELDS, RCA_FIELD_LABELS, CAR_REQUIRED_FIELDS, CAR_FIELD_LABELS } from './constants';
import { chainWhys, rcaFieldValue } from './whyChain';

/** CR-8706: where the edit page's labels differ from the create form's. */
const EDIT_LABELS: Record<string, string> = {
  defect_description: 'Description of Non-Conformity',
  containment_required: 'Containment requested?',
  containment_current_stock: 'Current Stock (at claim)',
  containment_qty_ok: 'OK Qty',
  containment_qty_not_ok: 'NOT OK Qty',
  assigned_to: 'Assigned To (internal team)',
};

interface UseNCRSubmitHandlersParams {
  /** NCR id from the route; the handlers no-op until it resolves. */
  id: string | undefined;
  formData: NCREditFormData;
  setSaving: (saving: boolean) => void;
  setRcaFieldErrors: (errors: Record<string, string>) => void;
  setCarFieldErrors: (errors: Record<string, string>) => void;
  /** CR-8706: fields the server refused on Save / Submit, keyed by field name. */
  setDetailFieldErrors: (errors: Record<string, string>) => void;
  /** Refetch the NCR; called with `true` so the Tabs tree stays mounted. */
  fetchNCRDetails: (isRefresh?: boolean) => Promise<NonConformanceReport | undefined>;
}

/**
 * Save-draft and submit actions for the NCR edit page: Save (all three
 * workflow steps' editable fields), Submit Detail, Submit RCA and Submit CAR,
 * including the inline required-field validation for RCA and CAR.
 *
 * @param params - route id, live form data, the saving / field-error setters
 *   and the NCR refetch callback owned by useNCREdit
 * @returns handleSave, handleSubmitDetail, handleSubmitRCA, handleSubmitCAR
 */
export function useNCRSubmitHandlers({
  id,
  formData,
  setSaving,
  setRcaFieldErrors,
  setCarFieldErrors,
  setDetailFieldErrors,
  fetchNCRDetails,
}: UseNCRSubmitHandlersParams) {
  /**
   * CR-8706: report a refused Save / Submit. Field refusals name their field
   * ("Reject Qty cannot be empty") and are kept for the form to highlight;
   * anything else falls back to the API's own message.
   */
  const reportSaveError = (error: unknown, fallback: string) => {
    const refused = getApiFieldErrors(error);
    const labelled: Record<string, string> = {};
    for (const [field, message] of Object.entries(refused)) {
      labelled[field] = ncrFieldMessage(field, message, EDIT_LABELS);
    }
    setDetailFieldErrors(labelled);
    const messages = Object.values(labelled).map((m) => (/[.!?]$/.test(m) ? m : `${m}.`));
    toast.error(messages.length ? messages.join(' ') : getApiErrorMessage(error, fallback));
  };

  /**
   * Save the NCR's editable fields.
   *
   * @param opts - `masterEdit`: the admin has Master Edit switched on. Without
   *   it every save is an ordinary amendment -- every check applies and nothing
   *   is logged as Master Edit (QUALITY-NCR-REVIEW rounds 3-4).
   */
  const handleSave = async (opts?: { masterEdit?: boolean }) => {
    if (!id) return;

    try {
      setSaving(true);
      // Build patch payload from editable fields
      const payload: Record<string, unknown> = {};
      // CR-6001: Save is shared by the Detail / RCA / CAR tabs, so the
      // payload has to carry the union of all three workflow steps'
      // fields. Previously only Detail-tab fields were listed, so the
      // CAR (and RCA) Save button fired an empty PATCH — the toast
      // said "Saved successfully" but the textareas re-mounted with
      // the unchanged server-side state and the user's edits vanished.
      const editableFields = [
        // Step 1 — Detail
        'grn_number', 'customer_reference', 'po_number', 'part_number', 'description',
        'batch_number', 'works_order', 'reject_qty', 'batch_qty',
        'unit_price', 'shortage_qty', 'severity', 'defect_description',
        'buyer_name', 'liability_type', 'liability_classification',
        'is_internal', 'assigned_to',
        'warehouse',  // CR-7347: receiving warehouse, editable in Order Information
        'is_8d_required', 'is_collection_required', 'is_credit_required',
        // is_replacement_required was missing from this whitelist, so the
        // Detail tab's "Replacement Required = Yes" never reached the API
        // and silently reverted to No on reload.
        'is_replacement_required',
        'proposed_rejection_type', 'disposition_quantity', 'action_required',
        'source_of_rejection_option', 'fault_group', 'fault_category',
        // CR-7069: 8D D1 Team Formation fields
        'team_leader', 'team_members', 'team_formation_date',
        // Step 2 — Root Cause Analysis (draft persistence; submit_rca still
        // owns the status transition created → ca_submitted)
        'disposition_instructions', 'immediate_corrective_action',
        // CR-7139: each chain as the list of Whys the user has.
        'whys', 'selected_root_cause',
        // CR-7353: the Escape root-cause chain.
        'escape_whys', 'escape_selected_root_cause',
        // Step 3 — Corrective Action Report (draft persistence; submit_car
        // still owns the status transition ca_submitted → car_submitted)
        'corrective_action', 'preventive_action',
        'escape_corrective_action', 'escape_preventive_action',  // CR-7354
        'impact_assessment',
        'escape_impact_assessment',  // CR-7353
        'similar_products_affected', 'impact_on_similar_products',
        'training_required', 'training_details',
        'doc_process_flow_updated', 'doc_control_plan_updated',
        'doc_fmea_updated', 'doc_work_instructions_updated',
        'doc_other_updated',
        'documentation_references', 'corrective_action_notes',
        'action_completion_date',
        'escape_action_completion_date',  // CR-7353
        // Customer review 2026-07: 8D extensions + containment status
        'commonised_corrective_action', 'horizontal_deployment_parts',
        'system_learning',  // CR-7355: 8D D7 System Learning
        'containment_required', 'containment_current_stock',
        'containment_qty_ok', 'containment_qty_not_ok',
        'containment_confirmed',
      ];
      for (const field of editableFields) {
        if (formData[field] !== undefined) {
          payload[field] = formData[field];
        }
      }
      if (opts?.masterEdit === true) payload['master_edit'] = true;
      await qualityAPI.updateNCR(parseInt(id), payload as Partial<NonConformanceReport>);
      setDetailFieldErrors({});
      toast.success('Saved successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error saving NCR:', error);
      // Round-1 review: say why (reject cap, locked field, started process...).
      reportSaveError(error, 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitDetail = async () => {
    if (!id) return;

    try {
      setSaving(true);
      // Save all form data first before transitioning status
      const payload: Record<string, unknown> = {};
      const editableFields = [
        'grn_number', 'customer_reference', 'po_number', 'part_number', 'description',
        'batch_number', 'works_order', 'reject_qty', 'batch_qty',
        'unit_price', 'shortage_qty', 'severity', 'defect_description',
        'buyer_name', 'liability_type', 'liability_classification',
        'is_internal', 'assigned_to',
        'warehouse',  // CR-7347: receiving warehouse, editable in Order Information
        'is_8d_required', 'is_collection_required', 'is_credit_required',
        // is_replacement_required was missing from this whitelist, so the
        // Detail tab's "Replacement Required = Yes" never reached the API
        // and silently reverted to No on reload.
        'is_replacement_required',
        'proposed_rejection_type', 'disposition_quantity', 'action_required',
        'source_of_rejection_option', 'fault_group', 'fault_category',
        // Customer review 2026-07: containment status lives on the Detail tab
        'containment_required', 'containment_current_stock',
        'containment_qty_ok', 'containment_qty_not_ok',
        'containment_confirmed',
      ];
      for (const field of editableFields) {
        if (formData[field] !== undefined) {
          payload[field] = formData[field];
        }
      }
      await qualityAPI.updateNCR(parseInt(id), payload as Partial<NonConformanceReport>);
      await qualityAPI.submitDetail(parseInt(id));
      setDetailFieldErrors({});
      toast.success('Detail submitted successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting detail:', error);
      reportSaveError(error, 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRCA = async () => {
    if (!id || !formData) return;

    // CR-5999: Field-level validation messaging. Build a Record of
    // missing fields, set it so the RCA tab renders inline red borders
    // and error text below each empty required textarea, and show a
    // toast summary using user-friendly labels (not raw API field
    // names like "why_1").
    const missingErrors: Record<string, string> = {};
    for (const field of RCA_REQUIRED_FIELDS) {
      const value = rcaFieldValue(formData, field);
      if (typeof value !== 'string' || !value.trim()) {
        missingErrors[field] = 'This field is required';
      }
    }

    if (Object.keys(missingErrors).length > 0) {
      setRcaFieldErrors(missingErrors);
      const friendlyLabels = Object.keys(missingErrors).map(
        (k) => RCA_FIELD_LABELS[k] || k
      );
      toast.error(
        `Please complete the following required fields: ${friendlyLabels.join(', ')}`
      );
      return;
    }

    // All fields populated — clear any prior error state.
    setRcaFieldErrors({});

    try {
      setSaving(true);
      await qualityAPI.submitRCA(parseInt(id), {
        // CR-7139: each chain as the list of Whys the user has.
        whys: chainWhys(formData, 'whys'),
        selected_root_cause: formData.selected_root_cause || '',
        // CR-7353: the Escape root-cause chain alongside the Occurrence one.
        escape_whys: chainWhys(formData, 'escape_whys'),
        escape_selected_root_cause: (formData.escape_selected_root_cause as string) || '',
        immediate_corrective_action: formData.immediate_corrective_action || '',
        disposition_instructions: formData.disposition_instructions,
        // W7: the D1 team is on this form; Submit used to drop it unless the
        // user had pressed Save first.
        team_leader: (formData['team_leader'] as string) || '',
        team_members: (formData['team_members'] as string) || '',
        team_formation_date: (formData['team_formation_date'] as string) || null,
      });
      toast.success('Root Cause Analysis submitted successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting RCA:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit RCA'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCAR = async () => {
    if (!id || !formData) return;

    // CR-6000: Field-level validation messaging. Build a Record of missing
    // fields, set it so the CAR tab renders inline red borders and error text
    // below each empty required textarea, and show a toast summary using
    // user-friendly labels (not raw API names like "preventive_action").
    // Mirrors the CR-5999 RCA pattern so the two supplier-facing submit flows
    // behave identically — and crucially gives a persistent on-screen surface
    // rather than a toast-only message that users can miss ("nothing happens").
    const missingErrors: Record<string, string> = {};
    for (const field of CAR_REQUIRED_FIELDS) {
      const value = formData[field as keyof typeof formData];
      if (typeof value !== 'string' || !value.trim()) {
        missingErrors[field] = 'This field is required';
      }
    }

    if (Object.keys(missingErrors).length > 0) {
      setCarFieldErrors(missingErrors);
      const friendlyLabels = Object.keys(missingErrors).map(
        (k) => CAR_FIELD_LABELS[k] || k
      );
      toast.error(
        `Please complete the following required fields: ${friendlyLabels.join(', ')}`
      );
      return;
    }

    // All fields populated — clear any prior error state.
    setCarFieldErrors({});

    try {
      setSaving(true);
      await qualityAPI.submitCAR(parseInt(id), {
        corrective_action: formData.corrective_action || '',
        preventive_action: formData.preventive_action || '',
        // CR-7354: the Escape pair alongside the Occurrence pair.
        escape_corrective_action: (formData.escape_corrective_action as string) || '',
        escape_preventive_action: (formData.escape_preventive_action as string) || '',
        impact_assessment: formData.impact_assessment || '',
        escape_impact_assessment: (formData.escape_impact_assessment as string) || '',  // CR-7353
        impact_on_similar_products: formData.impact_on_similar_products || '',
        similar_products_affected: formData.similar_products_affected || false,
        training_required: formData.training_required || false,
        training_details: formData.training_details,
        doc_process_flow_updated: formData.doc_process_flow_updated || false,
        doc_control_plan_updated: formData.doc_control_plan_updated || false,
        doc_fmea_updated: formData.doc_fmea_updated || false,
        doc_work_instructions_updated: formData.doc_work_instructions_updated || false,
        doc_other_updated: formData.doc_other_updated || '',
        documentation_references: formData.documentation_references || '',
        corrective_action_notes: formData.corrective_action_notes || '',
        // Customer review 2026-07: 8D extensions
        commonised_corrective_action: (formData.commonised_corrective_action as number | null) ?? null,
        horizontal_deployment_parts: (formData.horizontal_deployment_parts as string) || '',
        system_learning: (formData.system_learning as string) || '',  // CR-7355
        // W7: the completion date(s) are on this form; Submit used to drop them.
        action_completion_date: (formData.action_completion_date as string) || null,
        escape_action_completion_date: (formData.escape_action_completion_date as string) || null,
      });
      toast.success('Corrective Action Report submitted successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting CAR:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit CAR'));
    } finally {
      setSaving(false);
    }
  };

  return { handleSave, handleSubmitDetail, handleSubmitRCA, handleSubmitCAR };
}
